// Универсальный компонент таблицы с inline-редактированием
import React, { useEffect, useMemo, useCallback } from 'react';
import { Table, Button, Space, Typography, Tooltip, message, Modal } from 'antd';
import {
  SaveOutlined,
  UndoOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTableStore } from './useTableStore';
import { useDraft } from './useDraft';
import { EditableCell } from './EditableCell';
import { TableSummary } from './TableSummary';
import type { ColumnConfig, ChangeSet, Row } from './types';

const { Text } = Typography;

export interface EditableTableProps {
  columns: ColumnConfig[];
  dataSource: Row[];
  rowKey: string;
  loading?: boolean;
  onSave: (changes: ChangeSet) => Promise<void>;
  draftKey?: string;
  allowAdd?: boolean; // Разрешить добавление строк
  allowPaste?: boolean; // Разрешить paste из Excel
  allowDelete?: boolean; // Разрешить удаление строк
}

// Парсер данных из clipboard (Excel копирует как tab-separated)
// Использует все колонки (кроме formula) — для новых строк все поля редактируемые
const parseClipboardData = (text: string, columns: ColumnConfig[]): Partial<Row>[] => {
  const rows: Partial<Row>[] = [];
  const lines = text.trim().split('\n');
  // Берём все колонки кроме вычисляемых (formula) и служебных
  const dataColumns = columns.filter(c => !c.formula);

  for (const line of lines) {
    const values = line.split('\t');
    const row: Partial<Row> = {};

    values.forEach((value, index) => {
      if (index < dataColumns.length) {
        const col = dataColumns[index];
        const trimmed = value.trim();

        if (!trimmed) return; // Пропускаем пустые

        // Конвертируем значение в нужный тип
        if (col.type === 'number') {
          const num = parseFloat(trimmed.replace(',', '.').replace(/\s/g, ''));
          if (!isNaN(num)) {
            row[col.field] = num;
          }
        } else {
          row[col.field] = trimmed;
        }
      }
    });

    // Добавляем только если есть хоть какие-то данные
    if (Object.keys(row).length > 0) {
      rows.push(row);
    }
  }

  return rows;
};

export const EditableTable: React.FC<EditableTableProps> = ({
  columns,
  dataSource,
  rowKey,
  loading = false,
  onSave,
  draftKey = 'default',
  allowAdd = true,
  allowPaste = true,
  allowDelete = true,
}) => {
  const {
    changes,
    newRows,
    deletedRows,
    setOriginalData,
    updateCell,
    addRow,
    addRows,
    removeNewRow,
    markForDeletion,
    unmarkForDeletion,
    resetChanges,
    getComputedData,
    hasChanges,
    getChangeSet,
  } = useTableStore();

  const [saving, setSaving] = React.useState(false);
  const [lastSaveTime, setLastSaveTime] = React.useState<number>(0);
  const tableRef = React.useRef<HTMLDivElement>(null);

  // Инициализация данных
  useEffect(() => {
    setOriginalData(dataSource, rowKey, columns);
  }, [dataSource, rowKey, columns, setOriginalData]);

  // Hook для работы с черновиками
  const draft = useDraft({
    draftKey,
    changes,
    onRestore: (restoredChanges) => {
      restoredChanges.forEach((rowChanges, rowId) => {
        Object.entries(rowChanges).forEach(([field, value]) => {
          updateCell(rowId, field, value);
        });
      });
    },
  });

  // Получаем данные с изменениями и вычисленными колонками
  const computedData = useMemo(() => getComputedData(), [getComputedData, changes, newRows, deletedRows, dataSource]);

  // Проверяем, изменена ли ячейка
  const isCellChanged = (rowId: string, field: string): boolean => {
    const rowChanges = changes.get(rowId);
    return rowChanges ? field in rowChanges : false;
  };

  // Проверяем, является ли строка новой (по __tempId или rowKey начинается с __new_)
  const isNewRow = (record: Row): boolean => {
    return !!record.__tempId || String(record[rowKey]).startsWith('__new_');
  };

  // Получить ID строки для операций
  const getRowId = (record: Row): string => {
    return record.__tempId || String(record[rowKey]);
  };

  // Обработка paste из clipboard
  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (!allowPaste) return;

    const text = e.clipboardData?.getData('text');
    if (!text) return;

    // Проверяем, есть ли табуляции (признак копирования из Excel)
    if (!text.includes('\t') && !text.includes('\n')) return;

    e.preventDefault();

    const rows = parseClipboardData(text, columns);
    if (rows.length === 0) {
      message.warning('Не удалось распознать данные');
      return;
    }

    addRows(rows);
    message.success(`Добавлено строк: ${rows.length}`);
  }, [allowPaste, columns, addRows]);

  // Подписываемся на paste события
  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;

    container.addEventListener('paste', handlePaste as EventListener);
    return () => {
      container.removeEventListener('paste', handlePaste as EventListener);
    };
  }, [handlePaste]);

  // Выполнение сохранения
  const doSave = async () => {
    setSaving(true);
    try {
      const changeSet = getChangeSet();

      await onSave({
        created: changeSet.created.length > 0 ? changeSet.created : undefined,
        updated: Object.keys(changeSet.updated).length > 0 ? changeSet.updated : undefined,
        deleted: changeSet.deleted.length > 0 ? changeSet.deleted : undefined,
      });

      // Очищаем изменения после успешного сохранения
      resetChanges();
      draft.clearDraft();
      setLastSaveTime(0);
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      message.error('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  // Проверка дублей по ключевому полю
  const checkDuplicates = (): string[] => {
    const changeSet = getChangeSet();
    const existingKeys = new Set(
      dataSource
        .filter(row => !changeSet.deleted.includes(String(row[rowKey])))
        .map(row => String(row[rowKey]))
    );

    const newKeys: string[] = [];
    const duplicates: string[] = [];

    for (const row of changeSet.created) {
      const key = String(row[rowKey] || '');
      if (!key) continue;

      if (existingKeys.has(key) || newKeys.includes(key)) {
        duplicates.push(key);
      } else {
        newKeys.push(key);
      }
    }

    return duplicates;
  };

  // Обработка сохранения в БД
  const handleSave = () => {
    if (!hasChanges()) return;

    // Проверка дублей
    const duplicates = checkDuplicates();
    if (duplicates.length > 0) {
      const keyColumn = columns.find(c => c.field === rowKey);
      const fieldName = keyColumn?.title || rowKey;
      message.error(`Дублирование по полю "${fieldName}": ${duplicates.join(', ')}`);
      return;
    }

    const changeSet = getChangeSet();
    const deleteCount = changeSet.deleted.length;

    // Если есть удаления — показываем подтверждение
    if (deleteCount > 0) {
      Modal.confirm({
        title: 'Подтверждение удаления',
        content: `Будет удалено записей без возможности восстановления: ${deleteCount}. Продолжить?`,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: doSave,
      });
    } else {
      doSave();
    }
  };

  // Добавить пустую строку
  const handleAddRow = () => {
    addRow({});
  };

  // Удалить новую строку
  const handleRemoveNewRow = (rowId: string) => {
    removeNewRow(rowId);
  };

  // Обработка сохранения черновика
  const handleSaveDraft = () => {
    draft.saveDraft();
    setLastSaveTime(Date.now());
  };

  // Обработка отката изменений
  const handleReset = () => {
    resetChanges();
    draft.clearDraft();
    setLastSaveTime(0);
  };

  // Конфигурация колонок для Ant Design Table
  const antColumns: ColumnsType<Row> = useMemo(() => {
    const cols: ColumnsType<Row> = columns.map((col) => ({
      key: col.field,
      dataIndex: col.field,
      title: col.title,
      width: col.width,
      render: (value: any, record: Row) => {
        const id = getRowId(record);
        const isChanged = isCellChanged(id, col.field);
        const isNew = isNewRow(record);

        return (
          <EditableCell
            value={value}
            column={col}
            rowData={record}
            onChange={(newValue) => updateCell(id, col.field, newValue)}
            isChanged={isChanged}
            isNew={isNew}
          />
        );
      },
    }));

    // Добавляем колонку действий
    if (allowAdd || allowDelete) {
      cols.push({
        key: '__actions',
        title: '',
        width: 50,
        render: (_, record: Row) => {
          const id = getRowId(record);
          const isNew = isNewRow(record);
          const isDeleted = record.__deleted;

          // Новая строка — кнопка удаления из списка
          if (isNew) {
            return (
              <Tooltip title="Удалить строку">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveNewRow(id)}
                />
              </Tooltip>
            );
          }

          // Существующая строка
          if (allowDelete) {
            if (isDeleted) {
              // Помечена на удаление — кнопка отмены
              return (
                <Tooltip title="Отменить удаление">
                  <Button
                    type="text"
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={() => unmarkForDeletion(id)}
                  />
                </Tooltip>
              );
            } else {
              // Не помечена — кнопка удаления
              return (
                <Tooltip title="Удалить">
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => markForDeletion(id)}
                  />
                </Tooltip>
              );
            }
          }

          return null;
        },
      });
    }

    return cols;
  }, [columns, rowKey, changes, deletedRows, updateCell, allowAdd, allowDelete, markForDeletion, unmarkForDeletion]);

  // Форматирование времени последнего сохранения
  const formatLastSaveTime = () => {
    if (!lastSaveTime) return null;
    const time = new Date(lastSaveTime).toLocaleTimeString('ru-RU');
    return (
      <Text type="secondary" style={{ fontSize: '12px' }}>
        <ClockCircleOutlined /> Черновик сохранён в {time}
      </Text>
    );
  };

  return (
    <div ref={tableRef} tabIndex={0} style={{ outline: 'none' }}>
      {/* Панель управления */}
      <Space style={{ marginBottom: 16 }} wrap>
        {allowAdd && (
          <Tooltip title="Добавить строку (или Ctrl+V из Excel)">
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddRow}
            >
              Добавить строку
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Сохранить изменения в localStorage">
          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveDraft}
            disabled={!hasChanges()}
          >
            Сохранить черновик
          </Button>
        </Tooltip>

        <Tooltip title="Отменить все изменения">
          <Button
            icon={<UndoOutlined />}
            onClick={handleReset}
            disabled={!hasChanges()}
          >
            Сбросить
          </Button>
        </Tooltip>

        <Tooltip title="Сохранить изменения в базу данных">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges()}
          >
            Применить
          </Button>
        </Tooltip>

        {formatLastSaveTime()}
      </Space>

      {allowPaste && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: '12px' }}>
          Можно вставить данные из Excel (Ctrl+V)
        </Text>
      )}

      {/* Таблица */}
      <Table
        columns={antColumns}
        dataSource={computedData}
        rowKey={(record) => record.__tempId || String(record[rowKey])}
        loading={loading || saving}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200', '500'],
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
        }}
        summary={() => <TableSummary columns={columns} data={computedData} />}
        bordered
        size="middle"
        rowClassName={(record) => {
          if (isNewRow(record)) return 'new-row';
          if (record.__deleted) return 'deleted-row';
          return '';
        }}
      />

      <style>{`
        .new-row {
          background-color: #f6ffed !important;
        }
        .new-row:hover > td {
          background-color: #d9f7be !important;
        }
        .deleted-row {
          background-color: #fff2f0 !important;
          text-decoration: line-through;
          opacity: 0.7;
        }
        .deleted-row:hover > td {
          background-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};
